'use client';

import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import classNames from 'classnames';
import styles from './toggle.module.sass';

type ToggleSize = 'default' | 'small';

export interface ToggleProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'type' | 'onChange' | 'size'
  > {
  /** Whether the toggle is checked (on) */
  checked?: boolean;
  /** Callback fired when toggle state changes */
  onChange?: (checked: boolean) => void;
  /** Optional label text beside the toggle */
  label?: ReactNode;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Size of the toggle */
  size?: ToggleSize;
  /** Additional CSS class */
  className?: string;
}

const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      checked = false,
      onChange,
      label,
      disabled = false,
      size = 'default',
      className,
      ...rest
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.checked);
    };

    const classes = classNames(
      styles.toggle,
      {
        [styles.active]: checked,
        [styles.small]: size === 'small',
        [styles.disabled]: disabled,
      },
      className
    );

    return (
      <label className={styles.wrapper}>
        <input
          ref={ref}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className={classes}
          {...rest}
        />
        <span
          className={classNames(styles.track, { [styles.active]: checked })}
        />
      </label>
    );
  }
);

Toggle.displayName = 'Toggle';

export default Toggle;
